import { useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, BookOpen, Upload, X, Image, ArrowUpDown } from "lucide-react";
import BlogPreviewModal from "@/components/admin/BlogPreviewModal";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { format } from "date-fns";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  cover_image: string | null;
  author: string;
  published: boolean;
  created_at: string;
  view_count?: number;
}

const formatViews = (n: number) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "m";
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  return String(n);
};

const emptyForm = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  cover_image: "",
  author: "Plymhair Team",
  published: true,
};

function CoverImageUploader({
  value,
  onChange,
}: {
  value: string;
  onChange: (url: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }

    setUploading(true);
    const ext = file.name.split(".").pop();
    const path = `covers/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

    const { error } = await supabase.storage.from("blog-images").upload(path, file);
    if (error) {
      toast.error("Upload failed: " + error.message);
      setUploading(false);
      return;
    }

    const { data } = supabase.storage.from("blog-images").getPublicUrl(path);
    onChange(data.publicUrl);
    setUploading(false);
  };

  return (
    <div className="space-y-2">
      <Label>Cover Image</Label>
      {value ? (
        <div className="relative rounded-lg overflow-hidden border">
          <img src={value} alt="Cover preview" className="w-full h-40 object-cover" />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-7 w-7"
            onClick={() => onChange("")}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div
          className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => fileRef.current?.click()}
        >
          {uploading ? (
            <p className="text-sm text-muted-foreground">Uploading…</p>
          ) : (
            <>
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Click to upload an image</p>
              <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP — max 5MB</p>
            </>
          )}
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = "";
        }}
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="…or paste an image URL"
        className="text-xs"
      />
    </div>
  );
}

const AdminBlog = () => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<BlogPost | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [previewPost, setPreviewPost] = useState<BlogPost | null>(null);
  const [sortBy, setSortBy] = useState<"date" | "views">("date");

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["admin-blog"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts" as any)
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as unknown as BlogPost[];
    },
  });

  const sortedPosts = useMemo(() => {
    if (sortBy === "views") {
      return [...posts].sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
    }
    return posts;
  }, [posts, sortBy]);

  const totalViews = useMemo(
    () => posts.reduce((sum, p) => sum + (p.view_count || 0), 0),
    [posts]
  );

  const upsert = useMutation({
    mutationFn: async (post: typeof emptyForm & { id?: string }) => {
      const slug = post.slug || post.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      const payload = { ...post, slug, cover_image: post.cover_image || null };

      if (post.id) {
        const { error } = await (supabase.from("blog_posts" as any) as any).update(payload).eq("id", post.id);
        if (error) throw error;
      } else {
        const { error } = await (supabase.from("blog_posts" as any) as any).insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-blog"] });
      qc.invalidateQueries({ queryKey: ["blog-posts"] });
      toast.success(editing ? "Post updated" : "Post created");
      setEditing(null);
      setCreating(false);
      setForm(emptyForm);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase.from("blog_posts" as any) as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-blog"] });
      qc.invalidateQueries({ queryKey: ["blog-posts"] });
      toast.success("Post deleted");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const togglePublish = useMutation({
    mutationFn: async ({ id, published }: { id: string; published: boolean }) => {
      const { error } = await (supabase.from("blog_posts" as any) as any).update({ published }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-blog"] });
      qc.invalidateQueries({ queryKey: ["blog-posts"] });
    },
  });

  const openCreate = () => {
    setForm(emptyForm);
    setEditing(null);
    setCreating(true);
  };

  const openEdit = (post: BlogPost) => {
    setForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      cover_image: post.cover_image || "",
      author: post.author,
      published: post.published,
    });
    setEditing(post);
    setCreating(true);
  };

  const handleSave = () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    upsert.mutate(editing ? { ...form, id: editing.id } : form);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="w-6 h-6" /> Blog Posts
          </h1>
          <p className="text-muted-foreground text-sm">
            {posts.length} post{posts.length !== 1 ? "s" : ""} · {formatViews(totalViews)} total view{totalViews !== 1 ? "s" : ""}
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-1" /> New Post
        </Button>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground py-10 text-center">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No blog posts yet</p>
        </div>
      ) : (
        <div className="bg-card rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="p-3 font-medium">Title</th>
                <th className="p-3 font-medium hidden md:table-cell">Author</th>
                <th className="p-3 font-medium hidden md:table-cell">Date</th>
                <th className="p-3 font-medium text-center">
                  <button
                    type="button"
                    onClick={() => setSortBy(sortBy === "views" ? "date" : "views")}
                    className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                    title="Sort by views"
                  >
                    <Eye className="w-3.5 h-3.5" /> Views
                    <ArrowUpDown className={`w-3 h-3 ${sortBy === "views" ? "text-primary" : "opacity-40"}`} />
                  </button>
                </th>
                <th className="p-3 font-medium text-center">Published</th>
                <th className="p-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedPosts.map((post) => (
                <tr key={post.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {post.cover_image ? (
                        <img src={post.cover_image} alt="" className="w-10 h-10 rounded object-cover hidden sm:block" />
                      ) : (
                        <div className="w-10 h-10 rounded bg-muted flex items-center justify-center hidden sm:flex">
                          <Image className="w-4 h-4 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium truncate max-w-[200px] md:max-w-[300px]">{post.title}</p>
                        <p className="text-xs text-muted-foreground truncate max-w-[200px]">/blog/{post.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 hidden md:table-cell text-muted-foreground">{post.author}</td>
                  <td className="p-3 hidden md:table-cell text-muted-foreground">
                    {format(new Date(post.created_at), "d MMM yyyy")}
                  </td>
                  <td className="p-3 text-center">
                    <span className="inline-flex items-center gap-1 text-sm font-medium tabular-nums">
                      <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                      {formatViews(post.view_count || 0)}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <Switch
                      checked={post.published}
                      onCheckedChange={(checked) => togglePublish.mutate({ id: post.id, published: checked })}
                    />
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setPreviewPost(post)}
                        title="Preview post"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(post)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => {
                          if (window.confirm(`Delete "${post.title}"?`)) deleteMut.mutate(post.id);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={creating} onOpenChange={(o) => { if (!o) { setCreating(false); setEditing(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Post" : "New Blog Post"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Blog post title" />
            </div>
            <div>
              <Label>Slug</Label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated-from-title" />
              <p className="text-xs text-muted-foreground mt-1">Leave blank to auto-generate from title</p>
            </div>
            <div>
              <Label>Excerpt *</Label>
              <Textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Short summary for listing page" rows={2} />
            </div>
            <CoverImageUploader
              value={form.cover_image}
              onChange={(url) => setForm({ ...form, cover_image: url })}
            />
            <div>
              <Label>Author</Label>
              <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
            </div>
            <div>
              <Label>Content * <span className="text-xs text-muted-foreground">(Use ## for headings, ### for subheadings)</span></Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Write your blog post here..."
                rows={16}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">{form.content.split(/\s+/).filter(Boolean).length} words</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.published} onCheckedChange={(checked) => setForm({ ...form, published: checked })} />
              <Label>Published</Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>Cancel</Button>
              <Button onClick={handleSave} disabled={upsert.isPending}>
                {upsert.isPending ? "Saving…" : editing ? "Update Post" : "Create Post"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <BlogPreviewModal
        post={previewPost}
        open={!!previewPost}
        onOpenChange={(open) => { if (!open) setPreviewPost(null); }}
      />
    </div>
  );
};

export default AdminBlog;
